import { Configuration, configuration } from '@/config';
import clarityPromise, { ClarityInstance } from '@/services/clarity';
import logRocketPromise, { LogRocketInstance } from '@/services/logRocket';
import newRelicAgentPromise, { NewRelicAgentClass } from '@/services/newRelic';

interface NewRelicConfiguration extends Configuration {
  readonly newRelicAppId: string;
  readonly newRelicAccountId: string;
  readonly newRelicTrustKey: string;
  readonly newRelicLicenseKey: string;
  readonly newRelicBeacon: string;
  readonly newRelicErrorBeacon: string;
}

const isNewRelicConfiguration = (
  configuration: Configuration,
): configuration is NewRelicConfiguration => {
  return !!(
    configuration.newRelicAppId &&
    configuration.newRelicAccountId &&
    configuration.newRelicTrustKey &&
    configuration.newRelicLicenseKey &&
    configuration.newRelicBeacon &&
    configuration.newRelicErrorBeacon
  );
};

const isNewRelicSyntheticMonitor = (): boolean =>
  navigator.userAgent.includes('NewRelicSyntheticMonitorProdDashboard');

export const instrumentApplication = async (): Promise<void> => {
  if (configuration.logRocketAppId && !isNewRelicSyntheticMonitor()) {
    console.debug('configuring logrocket');
    const logRocket: LogRocketInstance = await logRocketPromise;
    logRocket.init(configuration.logRocketAppId);

    window.addEventListener('error', (event: ErrorEvent): void => {
      if (event.error instanceof Error) {
        logRocket.captureException(event.error, {
          extra: { source: 'window_error' },
        });
      }
    });

    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent): void => {
      const reason = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
      logRocket.captureException(reason, {
        extra: { source: 'unhandled_rejection' },
      });
    });
  }

  if (isNewRelicConfiguration(configuration)) {
    console.debug('configuring newrelic');

    const newRelicConfig = {
      init: {
        session_replay: {
          enabled: true,
          block_selector: '',
          mask_text_selector: '*',
          sampling_rate: 10.0,
          error_sampling_rate: 100.0,
          mask_all_inputs: true,
          collect_fonts: true,
          inline_images: false,
          fix_stylesheets: true,
          preload: false,
          mask_input_options: {},
        },
        distributed_tracing: {
          enabled: true,
        },
        privacy: {
          cookies_enabled: true,
        },
        ajax: {
          deny_list: ['bam.nr-data.net'],
        },
      },
      info: {
        beacon: configuration.newRelicBeacon,
        errorBeacon: configuration.newRelicErrorBeacon,
        licenseKey: configuration.newRelicLicenseKey,
        applicationID: configuration.newRelicAppId,
        sa: 1,
      },
      loader_config: {
        accountID: configuration.newRelicAccountId,
        trustKey: configuration.newRelicTrustKey,
        agentID: configuration.newRelicAppId,
        licenseKey: configuration.newRelicLicenseKey,
        applicationID: configuration.newRelicAppId,
      },
    };

    const NewRelicAgent: NewRelicAgentClass = await newRelicAgentPromise;
    new NewRelicAgent(newRelicConfig);
  }

  if (configuration.clarityProjectId) {
    console.debug('configuring clarity');
    const clarity: ClarityInstance = await clarityPromise;
    clarity.init(configuration.clarityProjectId);
  }
};
